// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { StoryboardCard, StoryboardScene } from './chatbotTypes';
import { Pill } from './ChatCard';

// The pole_storyboard fly-through PLAYER: walks the 3D camera (and, optionally, the nearest pano) through the
// ordered scenes, one pole at a time, showing each pole's status card. Interactivity is local (a scene index +
// play/pause); the viewer is driven through parent callbacks that dispatch Redux (mirrors ChatChart) — this
// component imports no Redux and holds no viewer refs, so it renders standalone in tests.

// Status → color, matching the Lambda's storyboard highlight (clear green / attention orange / make_ready red /
// unrated gray). Exported so the parent uses the SAME color for the per-scene map highlight.
export const STORYBOARD_STATUS_COLORS: Record<string, string> = {
  clear: '#2E7D32', attention: '#F9A825', make_ready: '#D32F2F', unrated: '#9E9E9E',
};
const STATUS_LABEL: Record<string, string> = {
  clear: 'CLEAR', attention: 'ATTENTION', make_ready: 'MAKE-READY', unrated: 'UNRATED',
};
const SUMMARY_ORDER = ['make_ready', 'attention', 'clear', 'unrated'] as const;

const mutedSx = { fontSize: '0.72rem', color: 'grey.400' } as const;
const textSx = { fontSize: '0.72rem', color: '#eee', overflowWrap: 'anywhere' as const };

const SceneCard: React.FC<{ scene: StoryboardScene }> = ({ scene }) => {
  const color = STORYBOARD_STATUS_COLORS[scene.status] ?? '#9E9E9E';
  const attachments = Object.entries(scene.attachments ?? {}).map(([kind, n]) => `${n} ${kind.toLowerCase()}`);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Pill color={color}>{STATUS_LABEL[scene.status] ?? scene.status.toUpperCase()}</Pill>
        <Typography sx={textSx}>Pole {String(scene.pole_id)}</Typography>
        {typeof scene.height_ft === 'number' && <Typography sx={mutedSx}>{scene.height_ft.toFixed(1)} ft</Typography>}
      </Box>
      {attachments.length > 0 && <Typography sx={textSx}>{attachments.join(' · ')}</Typography>}
      {scene.status_reason && <Typography sx={mutedSx}>{scene.status_reason}</Typography>}
      {!!scene.issues?.length && (
        <Table size="small">
          <TableBody>
            {scene.issues.map((item, i) => {
              // Content + position: two issues can be content-identical (two comms at the same height).
              const rowKey = `${item.severity}|${item.issue}|${i}`;
              return (
                <TableRow key={rowKey}>
                  <TableCell sx={{ color: '#eee', fontSize: '0.7rem', borderColor: 'rgba(255,255,255,0.1)', whiteSpace: 'normal' }}>
                    {item.issue} — <em>{item.action}</em>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

const StoryboardPlayer: React.FC<{
  card: StoryboardCard;
  // Fly the camera (+ open the pano when openPano) to this scene. Parent wires it to a Redux dispatch.
  onScene: (scene: StoryboardScene, openPano: boolean) => void;
  // Clear the per-scene highlight when the player leaves the DOM.
  onClose?: () => void;
}> = ({ card, onScene, onClose }) => {
  const scenes = card.scenes ?? [];
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [withPanos, setWithPanos] = useState(true);
  const last = Math.max(0, scenes.length - 1);
  const current = Math.min(index, last);
  // Read the LATEST pano toggle via a ref so flipping "Panos" doesn't re-run the scene effect (which would
  // re-fly the camera to the same pole). The toggle therefore affects the NEXT scene advance, not the current one.
  const withPanosRef = useRef(withPanos);
  withPanosRef.current = withPanos;

  // Drive the viewer to the current scene whenever it changes (fly the camera + open the pano if enabled).
  useEffect(() => {
    if (scenes.length) onScene(scenes[current], withPanosRef.current);
  }, [current, scenes, onScene]);

  // Clear the highlight when the player unmounts (onClose must be stable — the parent memoizes it).
  useEffect(() => () => onClose?.(), [onClose]);

  // Auto-advance while playing; stop at the last scene.
  useEffect(() => {
    if (!playing) return undefined;
    if (current >= last) { setPlaying(false); return undefined; }
    const timer = setTimeout(() => setIndex((i) => Math.min(i + 1, last)), 2600);
    return () => clearTimeout(timer);
  }, [playing, current, last]);

  if (!scenes.length) return <Typography sx={mutedSx}>No poles to walk through.</Typography>;

  const summary = card.status_summary ?? {};
  const summaryLine = SUMMARY_ORDER
    .filter((key) => summary[key])
    .map((key) => `${summary[key]} ${STATUS_LABEL[key].toLowerCase()}`)
    .join(' · ');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
      {summaryLine && <Typography sx={mutedSx}>{`${card.total_poles} poles · ${summaryLine}`}</Typography>}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Button size="small" disabled={current <= 0} onClick={() => { setPlaying(false); setIndex((i) => Math.max(0, i - 1)); }}>‹ Prev</Button>
        <Button size="small" onClick={() => setPlaying((p) => !p)}>{playing ? 'Pause' : 'Play'}</Button>
        <Button size="small" disabled={current >= last} onClick={() => { setPlaying(false); setIndex((i) => Math.min(last, i + 1)); }}>Next ›</Button>
        <Typography sx={{ ...mutedSx, ml: 0.5 }}>{`Scene ${current + 1} / ${scenes.length}`}</Typography>
        <Button size="small" sx={{ ml: 'auto' }} onClick={() => setWithPanos((v) => !v)}>{withPanos ? 'Panos: on' : 'Panos: off'}</Button>
      </Box>
      <SceneCard scene={scenes[current]} />
      {card.note && <Typography sx={mutedSx}>{card.note}</Typography>}
      {card.scope && <Typography sx={mutedSx}>{card.scope}</Typography>}
      {card.truncated && <Typography sx={mutedSx}>{`Showing ${scenes.length} of ${card.total_poles} poles — raise the limit for more.`}</Typography>}
    </Box>
  );
};

export default StoryboardPlayer;
